import { z } from 'zod'

const progressCallbackSchema = z.custom<(progress: unknown) => void>(
  (value) => value === undefined || typeof value === 'function',
)
const abortSignalSchema = z.custom<AbortSignal>(
  (value) => value === undefined || value instanceof AbortSignal,
)

const projectSnapshotSchema = z
  .object({
    projectId: z.string().min(1),
    mediaProjectId: z.string().min(1),
    name: z.string().min(1),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    fps: z.number().positive(),
  })
  .strict()

const jsonRecordSchema: z.ZodType<Record<string, unknown>> = z.record(z.string(), z.unknown())

const mediaMetadataSchema = z
  .object({
    id: z.string().min(1),
    projectId: z.string().optional(),
    storageType: z.string().min(1),
    fileSize: z.number().nonnegative(),
    fileName: z.string().min(1),
    mimeType: z.string().min(1),
    duration: z.number().nonnegative(),
    width: z.number().nonnegative(),
    height: z.number().nonnegative(),
    fps: z.number().nonnegative(),
    codec: z.string().nullable().optional(),
    bitrate: z.number().nonnegative(),
    tags: z.array(z.string()).optional(),
    createdAt: z.number().nonnegative(),
    updatedAt: z.number().nonnegative(),
  })
  .passthrough()

const transcriptSegmentSchema = z
  .object({
    text: z.string().min(1),
    start: z.number().nonnegative(),
    end: z.number().nonnegative(),
    words: z
      .array(
        z
          .object({
            text: z.string().min(1),
            start: z.number().nonnegative(),
            end: z.number().nonnegative(),
          })
          .strict(),
      )
      .optional(),
  })
  .strict()

const mediaTranscriptSchema = z
  .object({
    id: z.string().min(1),
    mediaId: z.string().min(1),
    model: z.string().min(1),
    language: z.string().nullable().optional(),
    text: z.string().min(1),
    segments: z.array(transcriptSegmentSchema),
  })
  .passthrough()

const assistantPlanStepSchema = z
  .object({
    id: z.string().min(1),
    type: z.string().min(1),
    title: z.string().min(1),
    status: z.enum(['pending', 'running', 'done', 'failed', 'skipped']),
    payload: z.record(z.string(), z.unknown()).optional(),
    errorMessage: z.string().optional(),
    progressPercent: z.number().optional(),
    progressLabel: z.string().nullable().optional(),
  })
  .strict()

const assistantPlanSchema = z
  .object({
    id: z.string().min(1),
    taskType: z.string().min(1),
    title: z.string().min(1),
    sourcePrompt: z.string().min(1),
    planner: z.enum(['deepseek', 'sampling', 'manual']),
    createdAt: z.number().nonnegative(),
    context: z.record(z.string(), z.unknown()),
    steps: z.array(assistantPlanStepSchema),
  })
  .strict()

const assistantArtifactSchema = z
  .object({
    id: z.string().min(1),
    kind: z.enum([
      'project',
      'image',
      'audio',
      'transcript',
      'captions',
      'music',
      'timeline',
      'export',
    ]),
    label: z.string().min(1),
    refId: z.string().min(1).optional(),
    data: z.record(z.string(), z.unknown()).optional(),
    createdAt: z.number().nonnegative(),
  })
  .strict()

const assistantJobLogEntrySchema = z
  .object({
    id: z.string().min(1),
    level: z.enum(['info', 'error']),
    message: z.string().min(1),
    stepId: z.string().min(1).optional(),
    createdAt: z.number().nonnegative(),
  })
  .strict()

const assistantJobSchema = z
  .object({
    id: z.string().min(1),
    planId: z.string().min(1),
    status: z.enum(['queued', 'running', 'done', 'failed', 'cancelled']),
    currentStepId: z.string().min(1).nullable(),
    startedAt: z.number().nonnegative(),
    finishedAt: z.number().nonnegative().nullable(),
    context: z.record(z.string(), z.unknown()),
    steps: z.array(assistantPlanStepSchema),
    artifacts: z.array(assistantArtifactSchema),
    logs: z.array(assistantJobLogEntrySchema),
  })
  .strict()

const renderProgressSchema = z
  .object({
    phase: z.string().optional(),
    progress: z.number().optional(),
    currentFrame: z.number().optional(),
    totalFrames: z.number().optional(),
  })
  .passthrough()

const emptyInputSchema = z.object({}).strict()

export const assistantToolSchemas = {
  getCurrentProject: {
    input: emptyInputSchema,
    output: projectSnapshotSchema,
  },
  readCurrentProjectDiskSnapshot: {
    input: emptyInputSchema,
    output: z
      .object({
        projectId: z.string().min(1),
        mediaProjectId: z.string().min(1),
        name: z.string().min(1),
        projectJsonPath: z.string().min(1),
        mediaLinksPath: z.string().min(1),
        renderQueuePath: z.string().min(1),
        project: jsonRecordSchema,
        mediaLinks: z
          .object({
            total: z.number().int().nonnegative(),
            mediaIds: z.array(z.string().min(1)),
          })
          .strict(),
        renderQueue: z
          .object({
            jobCount: z.number().int().nonnegative(),
            isPaused: z.boolean(),
            updatedAt: z.number().nullable(),
            jobs: z.array(jsonRecordSchema),
          })
          .strict(),
        exportFiles: z.array(
          z
            .object({
              name: z.string().min(1),
              size: z.number().nonnegative(),
              lastModified: z.number().nonnegative(),
            })
            .strict(),
        ),
      })
      .strict(),
  },
  analyzeCurrentProjectStructure: {
    input: emptyInputSchema,
    output: z
      .object({
        project: z
          .object({
            projectId: z.string().min(1),
            name: z.string(),
            description: z.string(),
            schemaVersion: z.number().nullable(),
            width: z.number().nullable(),
            height: z.number().nullable(),
            fps: z.number().nullable(),
            updatedAt: z.number().nullable(),
            thumbnailId: z.string().nullable(),
          })
          .strict(),
        paths: z
          .object({
            projectJsonPath: z.string().min(1),
            mediaLinksPath: z.string().min(1),
            renderQueuePath: z.string().min(1),
          })
          .strict(),
        timeline: z
          .object({
            trackCount: z.number().int().nonnegative(),
            itemCount: z.number().int().nonnegative(),
            transitionCount: z.number().int().nonnegative(),
            compositionCount: z.number().int().nonnegative(),
            totalDurationFrames: z.number().int().nonnegative(),
            totalDurationSeconds: z.number().nonnegative(),
            currentFrame: z.number().nullable(),
            zoomLevel: z.number().nullable(),
            scrollPosition: z.number().nullable(),
            itemTypeBreakdown: z.record(z.string(), z.number().int().nonnegative()),
            tracks: z.array(
              z
                .object({
                  id: z.string().min(1),
                  name: z.string().min(1),
                  kind: z.string().min(1),
                  order: z.number().nullable(),
                  itemCount: z.number().int().nonnegative(),
                })
                .strict(),
            ),
            items: z.array(
              z
                .object({
                  id: z.string().min(1),
                  type: z.string().min(1),
                  trackId: z.string().min(1),
                  label: z.string(),
                  fromFrame: z.number().nonnegative(),
                  durationInFrames: z.number().nonnegative(),
                  endFrame: z.number().nonnegative(),
                  mediaId: z.string().nullable(),
                  srcKind: z.enum(['blob', 'file', 'remote', 'unknown']).nullable(),
                })
                .strict(),
            ),
          })
          .strict(),
        mediaLinks: z
          .object({
            total: z.number().int().nonnegative(),
            mediaIds: z.array(z.string().min(1)),
            linkedButUnusedMediaIds: z.array(z.string().min(1)),
            timelineReferencedMediaIds: z.array(z.string().min(1)),
            timelineUnlinkedMediaIds: z.array(z.string().min(1)),
          })
          .strict(),
        exports: z
          .object({
            total: z.number().int().nonnegative(),
            files: z.array(
              z
                .object({
                  name: z.string().min(1),
                  size: z.number().nonnegative(),
                  lastModified: z.number().nonnegative(),
                })
                .strict(),
            ),
          })
          .strict(),
        renderQueue: z
          .object({
            jobCount: z.number().int().nonnegative(),
            isPaused: z.boolean(),
            updatedAt: z.number().nullable(),
          })
          .strict(),
        warnings: z.array(z.string()),
      })
      .strict(),
  },
  createProject: {
    input: z
      .object({
        name: z.string().trim().min(1).max(100),
        description: z.string().max(500).optional(),
        width: z.number().int().min(320).max(7680),
        height: z.number().int().min(240).max(4320),
        fps: z.number().int().min(1).max(240),
        backgroundColor: z
          .string()
          .regex(/^#[0-9A-Fa-f]{6}$/)
          .optional(),
      })
      .strict(),
    output: projectSnapshotSchema,
  },
  openProject: {
    input: z.object({ projectId: z.string().trim().min(1) }).strict(),
    output: projectSnapshotSchema,
  },
  listProjectMedia: {
    input: z
      .object({
        projectId: z.string().trim().min(1).optional(),
        refresh: z.boolean().optional(),
      })
      .strict(),
    output: z
      .object({
        projectId: z.string().min(1),
        total: z.number().int().nonnegative(),
        items: z.array(mediaMetadataSchema),
      })
      .strict(),
  },
  analyzeProjectMedia: {
    input: z
      .object({
        projectId: z.string().trim().min(1).optional(),
      })
      .strict(),
    output: z
      .object({
        project: projectSnapshotSchema,
        media: z
          .object({
            totalItems: z.number().int().nonnegative(),
            totalDurationSeconds: z.number().nonnegative(),
            totalFileSize: z.number().nonnegative(),
            transcriptReadyCount: z.number().int().nonnegative(),
            usedOnTimelineCount: z.number().int().nonnegative(),
            videoCount: z.number().int().nonnegative(),
            audioCount: z.number().int().nonnegative(),
            imageCount: z.number().int().nonnegative(),
            otherCount: z.number().int().nonnegative(),
          })
          .strict(),
        timeline: z
          .object({
            trackCount: z.number().int().nonnegative(),
            itemCount: z.number().int().nonnegative(),
            transitionCount: z.number().int().nonnegative(),
            totalDurationSeconds: z.number().nonnegative(),
            referencedMediaCount: z.number().int().nonnegative(),
          })
          .strict(),
        items: z.array(
          z
            .object({
              mediaId: z.string().min(1),
              fileName: z.string().min(1),
              mimeType: z.string().min(1),
              kind: z.enum(['video', 'audio', 'image', 'other']),
              durationSeconds: z.number().nonnegative(),
              width: z.number().nonnegative(),
              height: z.number().nonnegative(),
              fps: z.number().nonnegative(),
              fileSize: z.number().nonnegative(),
              tags: z.array(z.string()),
              hasTranscript: z.boolean(),
              isUsedOnTimeline: z.boolean(),
              timelineItemCount: z.number().int().nonnegative(),
            })
            .strict(),
        ),
      })
      .strict(),
  },
  importLocalMedia: {
    input: z
      .object({
        projectId: z.string().trim().min(1).optional(),
        url: z.string().trim().url().optional(),
      })
      .strict(),
    output: z.array(mediaMetadataSchema),
  },
  summarizeTimeline: {
    input: emptyInputSchema,
    output: z
      .object({
        fps: z.number().positive(),
        trackCount: z.number().int().nonnegative(),
        itemCount: z.number().int().nonnegative(),
        transitionCount: z.number().int().nonnegative(),
        totalDurationFrames: z.number().int().nonnegative(),
        totalDurationSeconds: z.number().nonnegative(),
        inPoint: z.number().nullable(),
        outPoint: z.number().nullable(),
        tracks: z.array(
          z
            .object({
              id: z.string().min(1),
              name: z.string().min(1),
              type: z.string().min(1),
              order: z.number(),
              itemCount: z.number().int().nonnegative(),
            })
            .strict(),
        ),
      })
      .strict(),
  },
  draftEditPlan: {
    input: z
      .object({
        prompt: z.string().trim().min(1),
        ttsVoice: z.string().trim().min(1).optional(),
        bgmMode: z.enum(['generate', 'selected', 'none']).optional(),
        selectedBgmMediaId: z.string().trim().min(1).nullable().optional(),
        selectedBgmMediaLabel: z.string().trim().min(1).nullable().optional(),
        selectedReferenceMediaIds: z.array(z.string().trim().min(1)).optional(),
        selectedReferenceMediaLabels: z.array(z.string().trim().min(1)).optional(),
      })
      .strict(),
    output: assistantPlanSchema,
  },
  executePlanStep: {
    input: z
      .object({
        plan: assistantPlanSchema,
        stepId: z.string().trim().min(1).optional(),
        stepIndex: z.number().int().nonnegative().optional(),
        contextOverride: z.record(z.string(), z.unknown()).optional(),
      })
      .strict()
      .superRefine((value, ctx) => {
        const selectorCount = (value.stepId ? 1 : 0) + (typeof value.stepIndex === 'number' ? 1 : 0)
        if (selectorCount !== 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Exactly one of stepId or stepIndex must be provided.',
            path: ['stepId'],
          })
        }
      }),
    output: z
      .object({
        job: assistantJobSchema,
        executedStep: assistantPlanStepSchema,
      })
      .strict(),
  },
  generateTtsAudio: {
    input: z
      .object({
        engine: z.literal('aliyun'),
        text: z.string().trim().min(1),
        voice: z.string().trim().min(1),
        languageType: z.string().trim().min(1).optional(),
      })
      .strict(),
    output: z
      .object({
        blob: z.instanceof(Blob),
        file: z.instanceof(File),
        duration: z.number().positive(),
        engine: z.literal('aliyun'),
      })
      .strict(),
  },
  generateBgm: {
    input: z
      .object({
        topic: z.string().trim().min(1),
        tone: z.enum(['natural', 'professional', 'friendly', 'energetic', 'calm']),
        durationSeconds: z.number().positive().max(60),
        model: z.string().trim().min(1).optional(),
        signal: abortSignalSchema.optional(),
        onProgress: progressCallbackSchema.optional(),
      })
      .strict(),
    output: z
      .object({
        blob: z.instanceof(Blob),
        file: z.instanceof(File),
        duration: z.number().positive(),
        prompt: z.string().min(1),
        model: z.string().min(1),
      })
      .strict(),
  },
  saveGeneratedAudioToMedia: {
    input: z
      .object({
        file: z.instanceof(File),
        projectId: z.string().trim().min(1),
        tags: z.array(z.string()).optional(),
        codec: z.string().trim().min(1).optional(),
      })
      .strict(),
    output: mediaMetadataSchema,
  },
  insertAudioToTimeline: {
    input: z
      .object({
        media: mediaMetadataSchema,
        blobUrl: z.string().trim().min(1).optional(),
        volumeDb: z.number().optional(),
      })
      .strict(),
    output: z
      .object({
        inserted: z.boolean(),
        audioItemId: z.string().nullable(),
      })
      .strict(),
  },
  attachBgmToTimeline: {
    input: z
      .object({
        media: mediaMetadataSchema,
        blobUrl: z.string().trim().min(1).optional(),
        totalDurationSeconds: z.number().positive(),
        volumeDb: z.number().optional(),
        placement: z.enum(['existing-compatible-track', 'new-bottom-track']).optional(),
      })
      .strict(),
    output: z
      .object({
        inserted: z.boolean(),
        audioItemIds: z.array(z.string().min(1)),
      })
      .strict(),
  },
  transcribeMedia: {
    input: z
      .object({
        mediaId: z.string().trim().min(1),
        language: z.string().trim().min(1).optional(),
        signal: abortSignalSchema.optional(),
        onProgress: progressCallbackSchema.optional(),
      })
      .strict(),
    output: mediaTranscriptSchema,
  },
  insertTranscriptAsCaptions: {
    input: z
      .object({
        mediaId: z.string().trim().min(1),
        clipIds: z.array(z.string().trim().min(1)).optional(),
        replaceExisting: z.boolean().optional(),
        preferredTrackAboveTrackId: z.string().trim().min(1).optional(),
      })
      .strict(),
    output: z
      .object({
        insertedItemCount: z.number().int().nonnegative(),
        removedItemCount: z.number().int().nonnegative(),
      })
      .strict(),
  },
  removeFillerWords: {
    input: z
      .object({
        itemIds: z.array(z.string().trim().min(1)).optional(),
        transcriptMediaId: z.string().trim().min(1).optional(),
        preset: z.enum(['conservative', 'balanced', 'aggressive']).optional(),
        dryRun: z.boolean().optional(),
      })
      .strict(),
    output: z
      .object({
        affectedItemCount: z.number().int().nonnegative(),
        removedRangeCount: z.number().int().nonnegative(),
        totalRemovedSeconds: z.number().nonnegative(),
        dryRun: z.boolean(),
      })
      .strict(),
  },
  removeSilence: {
    input: z
      .object({
        itemIds: z.array(z.string().trim().min(1)).optional(),
        transcriptMediaId: z.string().trim().min(1).optional(),
        thresholdDb: z.number().optional(),
        minSilenceMs: z.number().int().nonnegative().optional(),
        paddingMs: z.number().int().nonnegative().optional(),
        dryRun: z.boolean().optional(),
      })
      .strict(),
    output: z
      .object({
        affectedItemCount: z.number().int().nonnegative(),
        removedRangeCount: z.number().int().nonnegative(),
        totalRemovedSeconds: z.number().nonnegative(),
        dryRun: z.boolean(),
      })
      .strict(),
  },
  startExport: {
    input: z
      .object({
        settings: z.record(z.string(), z.unknown()).optional(),
        signal: abortSignalSchema.optional(),
        download: z.boolean().optional(),
        fileName: z.string().trim().min(1).optional(),
        onProgress: progressCallbackSchema.optional(),
      })
      .strict(),
    output: z
      .object({
        fileName: z.string().min(1),
        savedPath: z.string().min(1),
        mimeType: z.string().min(1),
        fileSize: z.number().nonnegative(),
        fileSizeLabel: z.string().min(1),
        duration: z.number().nonnegative(),
        downloadTriggered: z.boolean(),
      })
      .strict(),
  },
  getRuntimeCapabilities: {
    input: emptyInputSchema,
    output: z
      .object({
        hasWorker: z.boolean(),
        hasWebCodecs: z.boolean(),
        hasAudioEncoder: z.boolean(),
        hasVideoEncoder: z.boolean(),
        hasAudioDecoder: z.boolean(),
        hasOffscreenCanvas: z.boolean(),
        hasFileSystemAccess: z.boolean(),
        hasSharedArrayBuffer: z.boolean(),
        hasWorkspaceRoot: z.boolean(),
        canImportLocalMedia: z.boolean(),
        canPersistProjectData: z.boolean(),
        supportsCloudTts: z.boolean(),
        supportedTtsEngines: z.array(z.string().min(1)),
        supportsCloudMusicGen: z.boolean(),
        supportsCloudTranscription: z.boolean(),
        supportsLocalExport: z.boolean(),
        supportsProgressEvents: z.boolean(),
        supportsCancellation: z.boolean(),
      })
      .strict(),
  },
  replaceCaptionStyle: {
    input: z
      .object({
        source: z
          .object({
            mediaId: z.string().trim().min(1).optional(),
            clipIds: z.array(z.string().trim().min(1)).optional(),
            captionSourceType: z
              .enum(['transcript', 'ai-captions', 'subtitle-import', 'embedded-subtitles'])
              .optional(),
          })
          .strict()
          .optional(),
        style: z.object({
          fontFamily: z.string().trim().min(1).optional(),
          fontSize: z.number().int().positive().optional(),
          fontWeight: z.enum(['normal', 'medium', 'semibold', 'bold']).optional(),
          color: z.string().trim().min(1).optional(),
          backgroundColor: z.string().trim().min(1).optional(),
          backgroundRadius: z.number().int().nonnegative().optional(),
          textAlign: z.enum(['left', 'center', 'right']).optional(),
          verticalAlign: z.enum(['top', 'middle', 'bottom']).optional(),
          lineHeight: z.number().positive().optional(),
          letterSpacing: z.number().optional(),
          textPadding: z.number().int().nonnegative().optional(),
          stroke: z
            .object({
              width: z.number().nonnegative(),
              color: z.string().trim().min(1),
            })
            .strict()
            .optional(),
          textShadow: z
            .object({
              offsetX: z.number(),
              offsetY: z.number(),
              blur: z.number().nonnegative(),
              color: z.string().trim().min(1),
            })
            .strict()
            .optional(),
        }),
        dryRun: z.boolean().optional(),
      })
      .strict(),
    output: z
      .object({
        matchedItemCount: z.number().int().nonnegative(),
        updatedItemCount: z.number().int().nonnegative(),
        dryRun: z.boolean(),
      })
      .strict(),
  },
  addBrandWatermark: {
    input: z
      .object({
        mode: z.enum(['text', 'image']),

        text: z.string().trim().min(1).optional(),
        imageMediaId: z.string().trim().min(1).optional(),
        placement: z.enum(['top-left', 'top-right', 'bottom-left', 'bottom-right']),
        opacity: z.number().min(0).max(1).optional(),
        scale: z.number().positive().optional(),
        marginX: z.number().int().nonnegative().optional(),
        marginY: z.number().int().nonnegative().optional(),
        startSeconds: z.number().nonnegative().optional(),
        endSeconds: z.number().nonnegative().optional(),
        createTrackIfNeeded: z.boolean().optional(),
      })
      .strict(),
    output: z
      .object({
        inserted: z.boolean(),
        trackId: z.string().min(1),
        itemIds: z.array(z.string().min(1)),
      })
      .strict(),
  },
  createTrack: {
    input: z
      .object({
        kind: z.enum(['video', 'audio']),
        name: z.string().trim().min(1).optional(),
        position: z.enum(['top', 'bottom']).optional(),
        locked: z.boolean().optional(),
        syncLock: z.boolean().optional(),
        visible: z.boolean().optional(),
        muted: z.boolean().optional(),
      })
      .strict(),
    output: z
      .object({
        trackId: z.string().min(1),
        kind: z.enum(['video', 'audio']),
        name: z.string().min(1),
      })
      .strict(),
  },
  insertTextOverlay: {
    input: z
      .object({
        text: z.string().trim().min(1),
        fromSeconds: z.number().nonnegative(),
        durationSeconds: z.number().positive(),
        trackId: z.string().trim().min(1).optional(),
        createTrackIfNeeded: z.boolean().optional(),
        preset: z.string().trim().min(1).optional(),
        style: z
          .object({
            fontFamily: z.string().trim().min(1).optional(),
            fontSize: z.number().int().positive().optional(),
            fontWeight: z.enum(['normal', 'medium', 'semibold', 'bold']).optional(),
            color: z.string().trim().min(1).optional(),
            backgroundColor: z.string().trim().min(1).optional(),
          })
          .strict()
          .optional(),
      })
      .strict(),
    output: z
      .object({
        itemId: z.string().min(1),
        trackId: z.string().min(1),
      })
      .strict(),
  },
  insertImageOverlay: {
    input: z
      .object({
        mediaId: z.string().trim().min(1),
        fromSeconds: z.number().nonnegative(),
        durationSeconds: z.number().positive(),
        trackId: z.string().trim().min(1).optional(),
        createTrackIfNeeded: z.boolean().optional(),
        opacity: z.number().min(0).max(1).optional(),
        placement: z
          .enum(['top-left', 'top-right', 'bottom-left', 'bottom-right', 'center'])
          .optional(),
      })
      .strict(),
    output: z
      .object({
        itemId: z.string().min(1),
        trackId: z.string().min(1),
      })
      .strict(),
  },
  setItemTransform: {
    input: z
      .object({
        itemId: z.string().trim().min(1),
        transform: z.object({
          x: z.number().optional(),
          y: z.number().optional(),
          width: z.number().positive().optional(),
          height: z.number().positive().optional(),
          rotation: z.number().optional(),
          opacity: z.number().min(0).max(1).optional(),
        }),
      })
      .strict(),
    output: z
      .object({
        updated: z.boolean(),
      })
      .strict(),
  },
  addItemKeyframes: {
    input: z
      .object({
        itemId: z.string().trim().min(1),
        keyframes: z
          .array(
            z.object({
              property: z.string().trim().min(1),
              frame: z.number().int().nonnegative(),
              value: z.number(),
              easing: z.string().trim().min(1).optional(),
            }),
          )
          .min(1),
      })
      .strict(),
    output: z
      .object({
        addedKeyframeCount: z.number().int().nonnegative(),
      })
      .strict(),
  },
  applyTransition: {
    input: z
      .object({
        leftClipId: z.string().trim().min(1),
        rightClipId: z.string().trim().min(1),
        type: z.string().trim().min(1).optional(),
        presentation: z.string().trim().min(1).optional(),
        durationInFrames: z.number().int().positive().optional(),
        alignment: z.number().min(0).max(1).optional(),
      })
      .strict(),
    output: z
      .object({
        added: z.boolean(),
        transitionId: z.string().optional(),
      })
      .strict(),
  },
  applyItemEffectPreset: {
    input: z
      .object({
        itemId: z.string().trim().min(1),
        presetId: z.string().trim().min(1),
      })
      .strict(),
    output: z
      .object({
        applied: z.boolean(),
        effectCount: z.number().int().nonnegative(),
      })
      .strict(),
  },
  createMaskForItem: {
    input: z
      .object({
        targetItemId: z.string().trim().min(1),
        shapeType: z.enum(['rectangle', 'circle', 'ellipse', 'path']),
        maskType: z.enum(['clip', 'alpha']).optional(),
        feather: z.number().int().nonnegative().optional(),
        invert: z.boolean().optional(),
      })
      .strict(),
    output: z
      .object({
        created: z.boolean(),
      })
      .strict(),
  },
  trimClip: {
    input: z
      .object({
        itemId: z.string().trim().min(1),
        startTrimFrames: z.number().int().optional(),
        endTrimFrames: z.number().int().optional(),
        ripple: z.boolean().optional(),
      })
      .strict(),
    output: z
      .object({
        trimmed: z.boolean(),
      })
      .strict(),
  },
  splitClip: {
    input: z
      .object({
        itemId: z.string().trim().min(1),
        splitFrame: z.number().int().nonnegative(),
        ripple: z.boolean().optional(),
      })
      .strict(),
    output: z
      .object({
        split: z.boolean(),
        leftItemId: z.string().nullable(),
        rightItemId: z.string().nullable(),
      })
      .strict(),
  },
  setAudioVolume: {
    input: z
      .object({
        itemId: z.string().trim().min(1),
        volumeDb: z.number().min(-60).max(12),
      })
      .strict(),
    output: z.object({ updated: z.boolean() }).strict(),
  },
  setAudioFade: {
    input: z
      .object({
        itemId: z.string().trim().min(1),
        fadeInSeconds: z.number().nonnegative().optional(),
        fadeOutSeconds: z.number().nonnegative().optional(),
      })
      .strict(),
    output: z.object({ updated: z.boolean() }).strict(),
  },
  duplicateItem: {
    input: z
      .object({
        itemId: z.string().trim().min(1),
        targetTrackId: z.string().trim().min(1).optional(),
        fromSeconds: z.number().nonnegative().optional(),
        count: z.number().int().positive().optional(),
      })
      .strict(),
    output: z
      .object({
        duplicated: z.boolean(),
        newItemIds: z.array(z.string().min(1)),
      })
      .strict(),
  },
  getPlanStatus: {
    input: z.object({}).strict(),
    output: z
      .object({
        hasDraftPlan: z.boolean(),
        hasCurrentJob: z.boolean(),
        isRunning: z.boolean(),
        planTitle: z.string().nullable(),
        jobStatus: z.string().nullable(),
        currentStepId: z.string().nullable(),
        currentStepTitle: z.string().nullable(),
        currentStepStatus: z.string().nullable(),
        error: z.string().nullable(),
      })
      .strict(),
  },
  confirmPlan: {
    input: z.object({}).strict(),
    output: z
      .object({
        confirmed: z.boolean(),
        planId: z.string().nullable(),
        stepCount: z.number().int().nonnegative(),
      })
      .strict(),
  },
  rippleDeleteRange: {
    input: z
      .object({
        itemIds: z.array(z.string().trim().min(1)).optional(),
        trackId: z.string().trim().min(1).optional(),
        rangeStartFrame: z.number().int().nonnegative().optional(),
        rangeEndFrame: z.number().int().nonnegative().optional(),
      })
      .strict(),
    output: z
      .object({
        deleted: z.boolean(),
        deletedItemCount: z.number().int().nonnegative(),
      })
      .strict(),
  },
  freezeFrameAt: {
    input: z
      .object({
        itemId: z.string().trim().min(1),
        frame: z.number().int().nonnegative(),
        durationSeconds: z.number().positive().optional(),
      })
      .strict(),
    output: z
      .object({
        inserted: z.boolean(),
        freezeFrameItemId: z.string().nullable(),
      })
      .strict(),
  },
  setInOutPoints: {
    input: z
      .object({
        inPoint: z.number().int().nonnegative().nullable().optional(),
        outPoint: z.number().int().nonnegative().nullable().optional(),
        clear: z.boolean().optional(),
      })
      .strict(),
    output: z.object({ updated: z.boolean() }).strict(),
  },
  updateCaptionItems: {
    input: z
      .object({
        clipId: z.string().trim().min(1),
        captions: z
          .array(
            z.object({
              captionItemId: z.string().trim().min(1),
              text: z.string().trim().min(1).optional(),
              fromSeconds: z.number().nonnegative().optional(),
              durationSeconds: z.number().positive().optional(),
            }),
          )
          .min(1),
      })
      .strict(),
    output: z
      .object({
        updated: z.boolean(),
        updatedItemCount: z.number().int().nonnegative(),
      })
      .strict(),
  },
  renderProgress: renderProgressSchema,
} as const

export type AssistantToolSchemaMap = typeof assistantToolSchemas
